'use client';

/**
 * One-time welcome banner after first-run provisioning (design §04) or
 * onboarding graduation: "Welcome to {name}." + the credits confirmation.
 *
 * Shown once per session right after the setup flow flags a fresh workspace.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * sessionStorage flag for a just-created workspace. Written by the setup
 * flows, read once here — owned by the banner, not the writers.
 */
export const FRESH_WORKSPACE_KEY = 'propensify:fresh-workspace';

export function FreshWorkspaceBanner() {
  const [banner, setBanner] = useState<{ name: string; credits: string } | null>(null);

  // Mount-once external read (must run post-hydration; the cascading-render
  // concern behind the rule does not apply here).
  useEffect(() => {
    try {
      const value = window.sessionStorage.getItem(FRESH_WORKSPACE_KEY);
      if (value) {
        // First-run setup stores a plain workspace name; onboarding
        // graduation stores {name, credits} JSON. Anything unparseable reads
        // as a plain name with the first-run credits line.
        let name = value;
        let credits = '500 welcome credits added';
        try {
          const parsed: unknown = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && typeof (parsed as { name?: unknown }).name === 'string') {
            name = (parsed as { name: string }).name;
            const parsedCredits = (parsed as { credits?: unknown }).credits;
            if (typeof parsedCredits === 'string' && parsedCredits) credits = parsedCredits;
          }
        } catch {
          // plain name: keep defaults
        }
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setBanner({ name, credits });
        window.sessionStorage.removeItem(FRESH_WORKSPACE_KEY);
      }
    } catch {
      // ignore: banner is a nicety
    }
  }, []);

  if (!banner) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-3" data-testid="funnel-welcome-banner">
      <div className="flex-1">
        <p className="text-sm font-medium text-[var(--ink)]">Welcome to {banner.name}.</p>
        <p className="mt-0.5 text-[13px] text-[var(--body)]">
          Your workspace is ready.{' '}
          <span className="inline-flex items-center rounded-full bg-[var(--lime)] px-2 py-px text-[11px] font-semibold text-[var(--ink)]">
            {banner.credits}
          </span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => setBanner(null)}
        aria-label="Dismiss"
        className="rounded p-1 text-[var(--faint)] transition-colors hover:bg-[var(--band)] hover:text-[var(--ink)]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
